import React, { useEffect, useState } from "react";
import { api } from "@/skate/lib/api";
import { useAuth } from "@/skate/context/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import SkaterAvatar from "@/skate/components/SkaterAvatar";
import RankBadge from "@/skate/components/RankBadge";
import { tierForElo, FORMAT_LABEL } from "@/skate/lib/rank";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Swords, MapPin } from "lucide-react";

export const PlayerView = () => {
  const { userId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [matches, setMatches] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/users/${userId}`);
        setP(data);
        const m = await api.get(`/matches?user_id=${userId}&limit=20`);
        setMatches(m.data);
      } catch {}
    })();
  }, [userId]);

  if (!p) {
    return <div className="font-mono text-xs text-zinc-500 tracking-widest">LOADING…</div>;
  }
  const tier = tierForElo(p.elo);
  const wlRatio = p.wins + p.losses > 0 ? Math.round((p.wins / (p.wins + p.losses)) * 100) : 0;

  return (
    <div data-testid="player-view-page">
      <button onClick={() => navigate(-1)} className="font-mono text-[10px] tracking-widest text-zinc-500 mb-4 inline-flex items-center gap-1 hover:text-white">
        <ArrowLeft className="w-3 h-3" /> BACK
      </button>

      <div className="border border-zinc-800 bg-[#121212] p-6">
        <div className="flex items-start gap-4">
          <SkaterAvatar user={p} size={80} className="!w-20 !h-20" />
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-display text-3xl">@{p.username}</span>
              {p.verified && <span className="font-mono text-[9px] tracking-widest text-[#FACC15] border border-[#FACC15] px-1.5">✓</span>}
            </div>
            <div className="font-mono text-[10px] text-zinc-500 mt-1 flex items-center gap-2 tracking-wider">
              <MapPin className="w-3 h-3" /> {p.city} · {p.stance?.toUpperCase()}
            </div>
            {p.bio && <p className="text-sm text-zinc-300 mt-3">{p.bio}</p>}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 items-center mt-6 pt-6 border-t border-zinc-900">
          <div>
            <div className="font-mono text-[10px] tracking-widest" style={{color: tier.color}}>{tier.name}</div>
            <div className="font-display text-5xl mt-1" style={{color: tier.color}}>{p.elo}</div>
            <div className="font-mono text-[10px] text-zinc-500">ELO</div>
          </div>
          <div className="flex justify-end"><RankBadge elo={p.elo} size="lg" /></div>
        </div>

        <div className="grid grid-cols-3 gap-3 mt-6">
          <div className="border border-zinc-800 p-3">
            <div className="font-mono text-[9px] text-zinc-500 tracking-widest">W / L</div>
            <div className="font-display text-2xl mt-1">{p.wins}-{p.losses}</div>
          </div>
          <div className="border border-zinc-800 p-3">
            <div className="font-mono text-[9px] text-zinc-500 tracking-widest">W RATE</div>
            <div className="font-display text-2xl mt-1 text-[#FACC15]">{wlRatio}%</div>
          </div>
          <div className="border border-zinc-800 p-3">
            <div className="font-mono text-[9px] text-zinc-500 tracking-widest">REP</div>
            <div className="font-display text-2xl mt-1">{p.reputation}</div>
          </div>
        </div>

        {p.favorite_tricks?.length > 0 && (
          <div className="mt-6">
            <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-2">ARSENAL</div>
            <div className="flex flex-wrap gap-2">
              {p.favorite_tricks.map((t, i) => (
                <span key={i} className="border border-zinc-800 px-3 py-1 font-mono text-xs">{t}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      <Button
        onClick={() => navigate(`/skate/challenges/new/${p.user_id}`)}
        data-testid="challenge-this-skater-btn"
        className="w-full mt-4 bg-[#FACC15] hover:bg-[#FDE047] text-black rounded-none h-14 font-display tracking-widest text-lg"
      >
        <Swords className="w-4 h-4 mr-2" /> CHALLENGE @{p.username}
      </Button>

      {/* Recent matches */}
      <div className="mt-8">
        <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-3">RECENT MATCHES</div>
        {matches.length === 0 ? (
          <div className="border border-dashed border-zinc-800 p-6 text-center font-mono text-xs text-zinc-500 tracking-widest">
            NO MATCHES
          </div>
        ) : (
          <div className="border border-zinc-800 divide-y divide-zinc-900">
            {matches.map((m) => {
              const won = m.winner_id === p.user_id;
              const opp = won ? m.loser : m.winner;
              return (
                <div key={m.match_id} className="p-3 flex items-center gap-3">
                  <div className={`font-display text-xl w-8 ${won ? "text-green-500" : "text-[#EF4444]"}`}>
                    {won ? "W" : "L"}
                  </div>
                  <SkaterAvatar user={opp} size={32} />
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-sm truncate">vs @{opp?.username}</div>
                    <div className="font-mono text-[10px] text-zinc-500">{FORMAT_LABEL[m.format]} · {m.status}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PlayerView;
